import PropTypes from 'prop-types'
import {
    IconCopy,
    IconTrash,
    IconLayers,
    IconHistory,
    IconThikness,
    IconCornerRadius,
    IconOffset,
} from "../ToolSelectorTop/Icons.jsx";
import { RefreshCw, Boxes, Grid2X2, Grid2x2X } from "lucide-react";
import { useTools } from "../../ToolProvider.jsx";
import { useEditorTypeRightContext, useEditorTypeRightToggleContext, useSelectedTypeContext } from "../../SelectedTypeProvider.jsx";
import DropdownGroupObjects from "./DropdownGroupObjects.jsx";
import DropdownAlignDistribute from "./DropdownAlignDistribute.jsx";
import DropdownMirror from "./DropdownMirror.jsx";
import DropdownViewOrientation from "./DropdownViewOrientation.jsx";


const ToolSelectorLeft = ({
    modelID,
    onApply3dChanges,
    hasPending3dChanges = false,
    isRegenerating3d = false,
    hasRectSelection = false,
    selectedCount = 0,
    canGroup = false,
    canUngroup = false,
    isGridVisible3d = true,
    onToggleGrid3d = () => {},
    viewOrientation3d = 'flat',
    onViewOrientation3dChange = () => {},
}) => {

    const {
        onDeleteSelectedObject,
        onCopy,
    } = useTools();

    const editorTypeRight = useEditorTypeRightContext();
    const setEditorTypeRight = useEditorTypeRightToggleContext();

    const togglePanel = (type) => {
        setEditorTypeRight(prev =>
            prev.type === type
                ? { type: null, data: null }
                : { type, data: null }
        );
    };

    const selectedType = useSelectedTypeContext();

    if (selectedType === '3D') {
        return (
            <>
                <button
                    type="button"
                    onClick={onToggleGrid3d}
                    className={`btnMod tooltip tooltip-right ${isGridVisible3d ? '' : 'opacity-60'}`}
                    data-tip={isGridVisible3d ? 'Ocultar grid' : 'Mostrar grid'}
                    aria-pressed={isGridVisible3d}
                >
                    {isGridVisible3d
                        ? <Grid2X2 className="h-8 w-8" />
                        : <Grid2x2X className="h-8 w-8" />}
                </button>

                <DropdownViewOrientation
                    value={viewOrientation3d}
                    onChange={onViewOrientation3dChange}
                />

                <button
                    onClick={() => togglePanel('editor_params_3d')}
                    className={`btnMod tooltip tooltip-right ${editorTypeRight.type === 'editor_params_3d' ? 'ring-2 ring-primary' : ''}`}
                    data-tip="Editar parámetros 3D"
                >
                    <IconThikness />
                </button>

                <button
                    onClick={() => togglePanel('editor_pieces_3d')}
                    className={`btnMod tooltip tooltip-right ${editorTypeRight.type === 'editor_pieces_3d' ? 'ring-2 ring-primary' : ''}`}
                    data-tip="Ver piezas del modelo"
                >
                    <Boxes className="h-8 w-8" />
                </button>

                <button
                    onClick={onApply3dChanges}
                    disabled={!modelID || isRegenerating3d}
                    className={`btnMod tooltip tooltip-right relative ${hasPending3dChanges ? 'ring-2 ring-warning' : ''}`}
                    data-tip={hasPending3dChanges ? 'Aplicar cambios y regenerar modelo 3D' : 'Regenerar modelo 3D'}
                >
                    <RefreshCw className={`h-8 w-8 ${isRegenerating3d ? 'animate-spin' : ''}`} />
                    {hasPending3dChanges && !isRegenerating3d && (
                        <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-warning" />
                    )}
                </button>
            </>
        )
    }

    return (
        <>
            <button
                onClick={onCopy}
                className="btnMod tooltip tooltip-right"
                data-tip="Duplicar selección"
            >
                <IconCopy />
            </button>

            <button
                onClick={onDeleteSelectedObject}
                className="btnMod tooltip tooltip-right"
                data-tip="Borrar elementos seleccionados"
            >
                <IconTrash />
            </button>

            {/* Solo aplica con un grupo seleccionado (Desagrupar) o 2+ objetos
                (Agrupar) — con 0 o 1 objeto suelto no hay nada que hacer. */}
            {(canGroup || canUngroup) && (
                <DropdownGroupObjects canGroup={canGroup} canUngroup={canUngroup} />
            )}

            <DropdownAlignDistribute selectedCount={selectedCount} />

            <DropdownMirror selectedCount={selectedCount} />

            {/* Contextual: solo tiene sentido con un rectángulo seleccionado, y
                así se descubre justo cuando hace falta. El panel abierto sigue
                siendo válido mientras la selección exista. */}
            {(hasRectSelection || editorTypeRight.type === 'editor_shape') && (
                <button
                    onClick={() => togglePanel('editor_shape')}
                    className={`btnMod tooltip tooltip-right ${editorTypeRight.type === 'editor_shape' ? 'ring-2 ring-primary' : ''}`}
                    data-tip="Redondear las esquinas del rectángulo"
                >
                    <IconCornerRadius className="w-10 h-10" />
                </button>
            )}

            {/* Offset/fill como dibujo (C3D-16): de uso frecuente, así que
                siempre a mano; trabaja sobre la selección que haya. */}
            <button
                onClick={() => togglePanel('editor_offset_drawing')}
                className={`btnMod tooltip tooltip-right ${editorTypeRight.type === 'editor_offset_drawing' ? 'ring-2 ring-primary' : ''}`}
                data-tip="Contorno como dibujo (offset / fill)"
            >
                <IconOffset className="w-10 h-10" />
            </button>

            <button
                onClick={() => togglePanel('editor_layers')}
                className={`btnMod tooltip tooltip-right ${editorTypeRight.type === 'editor_layers' ? 'ring-2 ring-primary' : ''}`}
                data-tip="Gestión de capas"
            >
                <IconLayers />
            </button>

            <button
                onClick={() => togglePanel('editor_history')}
                className={`btnMod tooltip tooltip-right ${editorTypeRight.type === 'editor_history' ? 'ring-2 ring-primary' : ''}`}
                data-tip="Ver historial de cambios"
            >
                <IconHistory />
            </button>

        </>
    );
};

ToolSelectorLeft.propTypes = {
    modelID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onApply3dChanges: PropTypes.func.isRequired,
    hasPending3dChanges: PropTypes.bool,
    isRegenerating3d: PropTypes.bool,
    hasRectSelection: PropTypes.bool,
    selectedCount: PropTypes.number,
    canGroup: PropTypes.bool,
    canUngroup: PropTypes.bool,
    isGridVisible3d: PropTypes.bool,
    onToggleGrid3d: PropTypes.func,
    viewOrientation3d: PropTypes.string,
    onViewOrientation3dChange: PropTypes.func,
}

export default ToolSelectorLeft;
